import { useMemo } from "react";

import type { DirexIncident, DirexSketch as Sketch, Phase } from "../../api/types";
import { t } from "../../i18n/i18n";
import {
  FriendlyUnits,
  projection,
  SKETCH_BG,
  SKETCH_WIDTH,
  SketchGroundLayers,
  SketchMarks,
  unitBoxes,
  useSymbolIcons,
} from "../sketch/SketchBase";
import { KIND_COLOR } from "./common";

/** Croquis de situation de la DIREX : fond du camp, amis au temps choisi et incidents posés de ce temps (code
 *  dans une capsule aux couleurs de l'iPad). Un clic sur un incident le sélectionne. La carte principale
 *  n'est pas modifiée. */
export function DirexSketch({
  sketch,
  incidents,
  phase,
  selected,
  onSelect,
}: {
  sketch: Sketch;
  incidents: DirexIncident[];
  phase: Phase;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const p = useMemo(() => projection(sketch.bbox), [sketch.bbox]);
  const friends = sketch.ami[phase] ?? [];
  const icons = useSymbolIcons(friends.map((u) => u.sidc));
  const shown = incidents.filter((i) => i.tempo === phase && i.lat !== null && i.lon !== null);
  return (
    <svg
      viewBox={`0 0 ${SKETCH_WIDTH} ${p.height.toFixed(1)}`}
      className="block h-auto w-full"
      style={{ letterSpacing: "normal", fontStretch: "normal", background: SKETCH_BG }}
      role="img"
      aria-label={t("Croquis de situation en {phase}", { phase })}
    >
      <SketchGroundLayers p={p} ground={sketch} obstacles={unitBoxes(p, friends, icons)} />
      <FriendlyUnits p={p} units={friends} icons={icons} suffix={phase} />
      {shown.map((i) => {
        const x = p.x(i.lon as number);
        const y = p.y(i.lat as number);
        const color = KIND_COLOR[i.type];
        const w = i.code.length * 6.6 + 12;
        const active = i.id === selected;
        return (
          <g
            key={i.id}
            transform={`translate(${x} ${y})`}
            className="cursor-pointer"
            onClick={() => onSelect(i.id)}
            opacity={i.status === "traite" ? 0.45 : 1}
          >
            {active && <rect x={-w / 2 - 4} y={-12.5} width={w + 8} height={25} rx={12.5} fill="none" stroke="var(--color-accent)" strokeWidth={2} />}
            <rect x={-w / 2} y={-8.5} width={w} height={17} rx={8.5} fill={color.fill} stroke="var(--color-surface)" strokeWidth={1.6} />
            <text y={3.6} textAnchor="middle" fontSize={10} fontWeight={600} fill={color.text}>
              {i.code}
            </text>
            <title>{`${i.code} — ${i.short}`}</title>
          </g>
        );
      })}
      <SketchMarks p={p} />
    </svg>
  );
}
